import {
  ChainRegistryEntry,
  ChainRegistryListener,
  ChainRegistryNativeCurrency,
  ChainRegistryRaw,
  ChainRegistryToken,
} from './chainRegistryTypes';

type RegistrySource = 'empty' | 'remote';

interface RegistryState {
  entries: ChainRegistryEntry[];
  aliasIndex: Map<string, ChainRegistryEntry[]>;
  chainIdIndex: Map<number, ChainRegistryEntry>;
  idIndex: Map<string, ChainRegistryEntry>;
  source: RegistrySource;
}

const registryState: RegistryState = {
  entries: [],
  aliasIndex: new Map(),
  chainIdIndex: new Map(),
  idIndex: new Map(),
  source: 'empty',
};

const listeners = new Set<ChainRegistryListener>();

function normalizeString(input: unknown): string | null {
  if (input === null || input === undefined) {
    return null;
  }
  const value = String(input).trim();
  return value.length === 0 ? null : value;
}

function normalizeNumber(input: unknown): number | null {
  if (input === null || input === undefined) {
    return null;
  }
  const parsed = Number(input);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeUrl(input: unknown): string | null {
  const value = normalizeString(input);
  if (!value) {
    return null;
  }
  return value.replace(/\/$/u, '');
}

function normalizeNativeCurrency(
  raw: Partial<ChainRegistryNativeCurrency> | null | undefined,
): ChainRegistryNativeCurrency | null {
  if (!raw) {
    return null;
  }
  const name = normalizeString(raw.name);
  const symbol = normalizeString(raw.symbol);
  const decimals = normalizeNumber(raw.decimals);
  if (!name || !symbol || decimals === null) {
    return null;
  }
  return { name, symbol, decimals };
}

function normalizeToken(raw: Partial<ChainRegistryToken>): ChainRegistryToken | null {
  const symbol = normalizeString(raw.symbol);
  const name = normalizeString(raw.name) ?? symbol ?? null;
  const address = normalizeString(raw.address);
  const decimals = normalizeNumber(raw.decimals);
  if (!symbol || !name || !address || decimals === null) {
    return null;
  }
  const isActive = typeof raw.isActive === 'boolean' ? raw.isActive : undefined;
  const isTestnet = typeof raw.isTestnet === 'boolean' ? raw.isTestnet : undefined;
  return { symbol, name, address, decimals, isActive, isTestnet };
}

function uniquePush(collection: ChainRegistryEntry[], entry: ChainRegistryEntry): void {
  if (collection.some(item => item.id === entry.id)) {
    return;
  }
  collection.push(entry);
}

function normalizeChain(raw: ChainRegistryRaw): ChainRegistryEntry | null {
  const keySource = normalizeString(
    raw.key ??
      raw.code ??
      raw.chainCode ??
      raw.chain_code ??
      raw.chainName ??
      raw.chain_name ??
      raw.id
  );
  const chainId = normalizeNumber(raw.chainId ?? raw.chain_id);

  if (!keySource || chainId === null) {
    return null;
  }

  const initialKey = keySource.toLowerCase();
  const codeSource =
    normalizeString(
      raw.chainCode ??
        raw.chain_code ??
        raw.chainName ??
        raw.chain_name ??
        raw.code
    ) ?? initialKey;
  const code = codeSource.toLowerCase();
  const id = normalizeString(raw.id) ?? `${initialKey}-${chainId}`;
  const name = normalizeString(raw.name) ?? code.toUpperCase();
  const compatibility = normalizeString(raw.compatibility ?? raw.chain_compatibility)?.toLowerCase();
  if (!compatibility) {
    return null;
  }

  const isTestnetFlag = raw.isTestnet ?? raw.is_testnet;
  const isTestnet = typeof isTestnetFlag === 'boolean'
    ? isTestnetFlag
    : /test|sepolia|dev|sandbox/u.test(code);

  const scanBaseUrl = normalizeUrl(
    raw.scanBaseUrl ?? raw.scan_base_url ?? raw.blockExplorer ?? raw.block_explorer,
  );
  const blockExplorerUrl = normalizeUrl(
    raw.blockExplorer ?? raw.block_explorer ?? raw.scanBaseUrl ?? raw.scan_base_url,
  );
  const confirmationBlocks = normalizeNumber(
    raw.confirmationBlocks ?? raw.confirmation_blocks,
  );

  const aliasSet = new Set<string>();
  (raw.aliases ?? []).forEach(alias => {
    const normalized = normalizeString(alias);
    if (normalized) {
      aliasSet.add(normalized.toLowerCase());
    }
  });
  aliasSet.add(initialKey);
  aliasSet.add(code);
  [
    raw.chainName,
    raw.chain_name,
    raw.name,
  ].forEach(alias => {
    const normalized = normalizeString(alias);
    if (normalized) {
      aliasSet.add(normalized.toLowerCase());
    }
  });
  if (id) {
    aliasSet.add(id.toLowerCase());
  }
  aliasSet.add(String(chainId));
  aliasSet.add(`${initialKey}${chainId}`);

  const slug = code.replace(/[0-9]+$/u, '').replace(/-$/u, '');
  if (slug) {
    aliasSet.add(slug);
    aliasSet.add(slug.replace(/-/gu, '_'));
    aliasSet.add(slug.replace(/-/gu, ''));
  }

  const nativeCurrency = normalizeNativeCurrency(raw.nativeCurrency ?? raw.native_currency);
  if (!nativeCurrency) {
    return null;
  }
  const tokens: ChainRegistryToken[] = [];
  (raw.tokens ?? []).forEach(token => {
    const normalizedToken = normalizeToken(token);
    if (normalizedToken) {
      tokens.push(normalizedToken);
    }
  });

  const aliasArray = Array.from(aliasSet);

  let resolvedKey = aliasArray.find(alias => !alias.includes('-') && !alias.includes('_')) ?? aliasArray[0] ?? initialKey;

  if (isTestnet) {
    const preferredTestAlias = aliasArray.find(
      alias => /test|sepolia|amoy|dev|sandbox/u.test(alias) && !/\d+$/u.test(alias)
    );
    const fallbackTestAlias = aliasArray.find(alias => /test|sepolia|amoy|dev|sandbox/u.test(alias));
    if (preferredTestAlias) {
      resolvedKey = preferredTestAlias;
    } else if (fallbackTestAlias) {
      resolvedKey = fallbackTestAlias;
    } else if (slug) {
      resolvedKey = slug;
    }
  }

  if (!aliasSet.has(resolvedKey)) {
    aliasSet.add(resolvedKey);
  }

  return {
    id,
    key: resolvedKey,
    code,
    chainId,
    name,
    compatibility,
    isTestnet,
    network: isTestnet ? 'testnet' : 'mainnet',
    scanBaseUrl,
    blockExplorerUrl,
    confirmationBlocks,
    aliases: Array.from(aliasSet),
    nativeCurrency,
    tokens,
  };
}

function rebuildIndexes(entries: ChainRegistryEntry[], source: RegistrySource, notify: boolean): ChainRegistryEntry[] {
  if (entries.length === 0) {
    registryState.entries = [];
    registryState.aliasIndex.clear();
    registryState.chainIdIndex.clear();
    registryState.idIndex.clear();
    registryState.source = source;

    if (notify) {
      listeners.forEach(listener => {
        try {
          listener([]);
        } catch (error) {
          console.error('[ChainRegistryClient] listener error', error);
        }
      });
    }

    return [];
  }

  registryState.entries = entries
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));

  registryState.aliasIndex.clear();
  registryState.chainIdIndex.clear();
  registryState.idIndex.clear();
  registryState.source = source;

  registryState.entries.forEach(entry => {
    registryState.idIndex.set(entry.id, entry);
    registryState.chainIdIndex.set(entry.chainId, entry);

    const aliases = new Set<string>(entry.aliases);
    aliases.add(entry.key);
    aliases.add(entry.code);
    aliases.add(String(entry.chainId));
    aliases.add(`${entry.key}${entry.chainId}`);

    aliases.forEach(alias => {
      const normalized = alias.trim().toLowerCase();
      if (!normalized) {
        return;
      }
      const existing = registryState.aliasIndex.get(normalized) ?? [];
      uniquePush(existing, entry);
      registryState.aliasIndex.set(normalized, existing);
    });
  });

  if (notify) {
    const snapshot = registryState.entries.slice();
    listeners.forEach(listener => {
      try {
        listener(snapshot);
      } catch (error) {
        console.error('[ChainRegistryClient] listener error', error);
      }
    });
  }

  return registryState.entries.slice();
}

export function hydrateChainRegistry(
  rawChains: ChainRegistryRaw[],
  options?: { append?: boolean; notify?: boolean; allowEmptyReplace?: boolean },
): ChainRegistryEntry[] {
  const { append = false, notify = true, allowEmptyReplace = false } = options ?? {};
  if (!Array.isArray(rawChains)) {
    return registryState.entries.slice();
  }

  const normalizedEntries = rawChains
    .map(normalizeChain)
    .filter((entry): entry is ChainRegistryEntry => entry !== null);

  if (normalizedEntries.length === 0 && !allowEmptyReplace) {
    return registryState.entries.slice();
  }

  if (append) {
    const merged = registryState.entries.concat(normalizedEntries);
    const deduplicated = new Map<string, ChainRegistryEntry>();
    merged.forEach(entry => {
      deduplicated.set(entry.id, entry);
    });
    return rebuildIndexes(Array.from(deduplicated.values()), 'remote', notify);
  }

  if (normalizedEntries.length === 0 && allowEmptyReplace) {
    return rebuildIndexes([], 'remote', notify);
  }

  return rebuildIndexes(normalizedEntries, 'remote', notify);
}

export function listChainRegistryEntries(): ChainRegistryEntry[] {
  return registryState.entries.slice();
}

export function getChainRegistrySource(): RegistrySource {
  return registryState.source;
}

export function getChainById(id: string): ChainRegistryEntry | undefined {
  const normalized = normalizeString(id);
  if (!normalized) {
    return undefined;
  }
  return registryState.idIndex.get(normalized);
}

export function getChainByAlias(
  identifier: string | number,
  chainId?: number | string,
): ChainRegistryEntry | undefined {
  const normalizedIdentifier = normalizeString(identifier);
  if (!normalizedIdentifier) {
    return undefined;
  }
  const aliasMatches = registryState.aliasIndex.get(normalizedIdentifier.toLowerCase());
  if (!aliasMatches || aliasMatches.length === 0) {
    return undefined;
  }

  if (chainId !== undefined) {
    const parsedChainId = normalizeNumber(chainId);
    if (parsedChainId !== null) {
      const directMatch = aliasMatches.find(entry => entry.chainId === parsedChainId);
      if (directMatch) {
        return directMatch;
      }
    }
  }

  if (aliasMatches.length === 1) {
    return aliasMatches[0];
  }

  const testnetMatch = aliasMatches.find(entry => entry.isTestnet);
  return testnetMatch ?? aliasMatches[0];
}

export function getChainByChainId(chainId: number | string): ChainRegistryEntry | undefined {
  const parsed = normalizeNumber(chainId);
  if (parsed === null) {
    return undefined;
  }
  return registryState.chainIdIndex.get(parsed);
}

export function onChainRegistryUpdate(listener: ChainRegistryListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function resetChainRegistry(): void {
  registryState.entries = [];
  registryState.aliasIndex.clear();
  registryState.chainIdIndex.clear();
  registryState.idIndex.clear();
  registryState.source = 'empty';
}

export type {
  ChainRegistryEntry,
  ChainRegistryListener,
  ChainRegistryNativeCurrency,
  ChainRegistryToken,
  ChainRegistryRaw,
} from './chainRegistryTypes';
